import React from 'react';
import { motion } from 'framer-motion';
import { FiTarget, FiTrendingUp, FiZap, FiRotateCcw, FiAward } from 'react-icons/fi';
import './Statistics.css';

const Statistics = ({ stats, totalCards, onReset }) => {
  const accuracy = stats.cardsStudied > 0 ? Math.round((stats.correctAnswers / stats.cardsStudied) * 100) : 0;
  
  const statCards = [
    {
      title: 'Cards Studied',
      value: stats.cardsStudied,
      icon: FiTarget,
      color: '#007AFF',
      bgColor: 'rgba(0, 122, 255, 0.1)'
    },
    {
      title: 'Accuracy',
      value: `${accuracy}%`,
      icon: FiTrendingUp,
      color: '#34C759',
      bgColor: 'rgba(52, 199, 89, 0.1)'
    },
    {
      title: 'Current Streak',
      value: stats.streak,
      icon: FiZap,
      color: '#FF9500',
      bgColor: 'rgba(255, 149, 0, 0.1)'
    },
    {
      title: 'Best Streak',
      value: stats.bestStreak,
      icon: FiAward,
      color: '#5856D6',
      bgColor: 'rgba(88, 86, 214, 0.1)'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24
      }
    }
  };

  return (
    <motion.div 
      className="statistics-container"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div className="stats-header" variants={itemVariants}>
        <h1>Your Progress</h1>
        <p>Keep up the great work! Here's how you're doing.</p>
      </motion.div>

      <motion.div className="stats-grid" variants={itemVariants}>
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.title}
              className="stat-card"
              variants={itemVariants}
              whileHover={{ scale: 1.02, y: -5 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
            >
              <div 
                className="stat-icon"
                style={{ backgroundColor: stat.bgColor }}
              >
                <Icon style={{ color: stat.color }} />
              </div>
              <div className="stat-content">
                <h3 className="stat-value" style={{ color: stat.color }}>
                  {stat.value}
                </h3>
                <p className="stat-title">{stat.title}</p>
              </div>
            </motion.div>
          );
        })}
      </motion.div>

      <motion.div className="progress-section" variants={itemVariants}>
        <h2>Overall Progress</h2>
        <div className="progress-card">
          <div className="progress-info">
            <span className="progress-label">Completion Rate</span>
            <span className="progress-percentage">{accuracy}%</span>
          </div>
          <div className="progress-bar-container">
            <motion.div 
              className="progress-bar-fill"
              initial={{ width: 0 }}
              animate={{ width: `${accuracy}%` }}
              transition={{ duration: 1, ease: "easeOut" }}
              style={{ 
                background: `linear-gradient(90deg, #34C759 0%, #30D158 100%)` 
              }}
            />
          </div>
          <div className="progress-details">
            <span>{stats.correctAnswers} correct out of {stats.cardsStudied} studied</span>
          </div>
        </div>
      </motion.div>

      <motion.div className="achievements-section" variants={itemVariants}>
        <h2>Achievements</h2>
        <div className="achievements-grid">
          <div className={`achievement ${stats.cardsStudied >= 10 ? 'unlocked' : 'locked'}`}>
            <div className="achievement-icon">🎯</div>
            <div className="achievement-content">
              <h4>First Steps</h4>
              <p>Study 10 cards</p>
            </div>
          </div>
          <div className={`achievement ${stats.bestStreak >= 5 ? 'unlocked' : 'locked'}`}>
            <div className="achievement-icon">🔥</div>
            <div className="achievement-content">
              <h4>On Fire</h4>
              <p>5 card streak</p>
            </div>
          </div>
          <div className={`achievement ${accuracy >= 80 && stats.cardsStudied >= 20 ? 'unlocked' : 'locked'}`}>
            <div className="achievement-icon">⭐</div>
            <div className="achievement-content">
              <h4>Expert</h4>
              <p>80% accuracy</p>
            </div>
          </div>
          <div className={`achievement ${stats.bestStreak >= 10 ? 'unlocked' : 'locked'}`}>
            <div className="achievement-icon">🏆</div>
            <div className="achievement-content">
              <h4>Champion</h4>
              <p>10 card streak</p>
            </div>
          </div>
        </div>
      </motion.div>

      <motion.div className="reset-section" variants={itemVariants}>
        <motion.button
          className="reset-btn"
          onClick={onReset}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <FiRotateCcw />
          <span>Reset Statistics</span>
        </motion.button>
      </motion.div>
    </motion.div>
  );
};

export default Statistics; 